import React, { useState, useEffect } from 'react';
import { api } from '../../utils/requestInterceptor';
import { useNavigate } from 'react-router-dom';
import ViewsNavigation from './ViewsNavigation';
import { useRaciAssignments } from '../../hooks/useRaciAssignments';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import format from 'date-fns/format';
import parse from 'date-fns/parse';
import startOfWeek from 'date-fns/startOfWeek';
import getDay from 'date-fns/getDay';
import { createCalendarDate, getDueDateStatusColor } from '../../utils/dateUtils';

// Setup the calendar localizer
const locales = {
  'en-US': require('date-fns/locale/en-US')
};

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales
});

// Helper function to format dates
const formatDate = (dateString) => {
  if (!dateString) return 'No date';
  try {
    return new Date(dateString).toLocaleDateString();
  } catch (e) {
    return 'Invalid date';
  }
};

// Helper function to get due date status color for list items (returns CSS classes)
const getDueDateStatusColorClasses = (dueDate) => {
  if (!dueDate) return 'bg-gray-100 text-gray-700';
  
  try {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const diffTime = new Date(dueDate).getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) {
      return 'bg-red-100 text-red-800'; // Overdue
    } else if (diffDays <= 7) {
      return 'bg-orange-100 text-orange-800'; // Due soon
    } else {
      return 'bg-green-100 text-green-800'; // Due later
    }
  } catch (e) {
    return 'bg-gray-100 text-gray-700';
  }
};

// Helper to get due date status text
const getDueDateStatus = (dueDate) => {
  if (!dueDate) return { color: 'gray', text: 'No due date' };
  
  try {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const diffTime = new Date(dueDate).getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) {
      return { 
        color: 'red', 
        text: `Overdue by ${Math.abs(diffDays)} day${Math.abs(diffDays) !== 1 ? 's' : ''}` 
      };
    } else if (diffDays <= 7) {
      return { 
        color: 'orange',  
        text: diffDays === 0 ? 'Due today' : `Due in ${diffDays} day${diffDays !== 1 ? 's' : ''}` 
      };
    } else {
      return { 
        color: 'green', 
        text: `Due in ${diffDays} days` 
      };
    }
  } catch (e) {
    return { color: 'gray', text: 'Invalid date' };
  }
};

function WorkloadReport() {
  const [workloadData, setWorkloadData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedDepartmentId, setSelectedDepartmentId] = useState('all');
  const [selectedUserId, setSelectedUserId] = useState('all');
  const [view, setView] = useState('steps'); // 'steps' or 'phases'
  const [displayMode, setDisplayMode] = useState('list'); // 'list' or 'calendar'
  const [calendarView, setCalendarView] = useState('month'); // 'month', 'week', or 'day'
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [showUnassigned, setShowUnassigned] = useState(false);
  const navigate = useNavigate();
  const { getBatchAssignments } = useRaciAssignments();

  // Get initials from a user's name
  const getInitials = (name) => {
    if (!name) return '';
    
    const names = name.split(' ');
    if (names.length === 1) return names[0].charAt(0).toUpperCase();
    
    return (names[0].charAt(0) + names[names.length - 1].charAt(0)).toUpperCase();
  };

  // User colors for consistent color coding
  const userColors = {};
  const getColorForUser = (userId) => {
    if (userColors[userId]) return userColors[userId];
    
    // Generate a consistent color based on the userId
    const colorOptions = [
      '#3B82F6', // blue-500
      '#8B5CF6', // violet-500
      '#EC4899', // pink-500
      '#14B8A6', // teal-500
      '#F59E0B', // amber-500
      '#6366F1', // indigo-500
      '#10B981', // emerald-500
      '#06B6D4', // cyan-500
      '#A855F7', // purple-500
      '#F43F5E', // rose-500
    ];
    
    // Deterministic selection based on userId
    const index = parseInt(userId) % colorOptions.length;
    userColors[userId] = colorOptions[index];
    
    return userColors[userId];
  };

  // Fetch departments
  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        const response = await api.get('/departments');
        if (response.data && response.data.departments) {
          setDepartments(response.data.departments);
        }
      } catch (error) {
        console.error('Error fetching departments:', error);
      }
    };
    
    fetchDepartments();
  }, []);

  // Fetch users
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await api.get('/auth/users');
        if (response.data && response.data.users) {
          setUsers(response.data.users);
        }
      } catch (error) {
        console.error('Error fetching users:', error);
      }
    };
    
    fetchUsers();
  }, []);

  // Fetch workload data based on the selected view (steps or phases)
  useEffect(() => {
    const fetchWorkloadData = async () => {
      if (users.length === 0) return;
      
      try {
        setLoading(true);
        setError(null);
        
        // Get all projects first
        const projectsResponse = await api.get('/projects');
        const allProjects = projectsResponse.data.projects || [];
        
        // Filter projects by department if selected
        const filteredProjects = selectedDepartmentId === 'all'
          ? allProjects
          : allProjects.filter(p => p.department_id === parseInt(selectedDepartmentId));
        
        // Get all project IDs
        const projectIds = filteredProjects.map(project => project.id);
        
        // Fetch all assignments for these projects in a single batch call
        const batchAssignments = await getBatchAssignments('project', projectIds);
        
        // Process data based on selected view
        if (view === 'steps') {
          await processStepsWorkload(filteredProjects, batchAssignments);
        } else {
          await processPhasesWorkload(filteredProjects, batchAssignments);
        }
      } catch (error) {
        console.error('Error fetching workload data:', error);
        setError('Failed to load workload data');
        setLoading(false);
      }
    };

    fetchWorkloadData();
  }, [view, selectedDepartmentId, selectedUserId, users, getBatchAssignments]);

  // Process steps workload data
  const processStepsWorkload = async (projects, batchAssignments) => {
    try {
      // Map to track user workloads
      const userWorkloads = {};
      
      // Track unassigned items specifically
      const unassignedItems = [];
      
      // Process each project
      for (const project of projects) {
        // Skip if no assignments data
        if (!batchAssignments[project.id]) continue;
        
        // Process all phases in this project
        for (const phase of (project.phases || [])) {
          // Process all steps in this phase
          for (const step of (phase.steps || [])) {
            // Skip based on status (exclude completed/cancelled)
            const status = step.status ? step.status.toLowerCase() : '';
            if (status.includes('complete') || 
                status.includes('finished') || 
                status.includes('done') || 
                status.includes('cancel') || 
                status.includes('abandon')) {
              continue;
            }
            
            // Get assignments for this step
            const stepAssignments = batchAssignments[project.id]?.steps?.[step.id]?.assignments;
            
            // Check for responsible assignments
            const responsibleAssignments = stepAssignments?.R || [];
            
            // If there are no responsible assignments and we're showing unassigned
            if (responsibleAssignments.length === 0) {
              // Only include if we're specifically looking at unassigned or showing all
              if (selectedUserId === 'unassigned' || selectedUserId === 'all') {
                // Get due date status
                const dueStatus = getDueDateStatus(step.due_date);
                
                // Add to unassigned items
                unassignedItems.push({
                  id: step.id,
                  type: 'step',
                  name: step.name,
                  projectId: project.id,
                  projectName: project.name,
                  phaseId: phase.id,
                  phaseName: phase.name,
                  dueDate: step.due_date,
                  dueStatus,
                  status: step.status || 'Not set',
                  departmentId: project.department_id,
                  departmentName: departments.find(d => d.id === project.department_id)?.name || 'Unknown'
                });
              }
              continue;
            }
            
            // Filter by user if needed
            const filteredAssignments = selectedUserId === 'all' || selectedUserId === 'unassigned'
              ? responsibleAssignments
              : responsibleAssignments.filter(a => a.user_id === parseInt(selectedUserId));
            
            // Skip if no matching assignments
            if (filteredAssignments.length === 0) continue;
            
            // Process assignments for each user
            filteredAssignments.forEach(assignment => {
              const userId = assignment.user_id;
              const user = users.find(u => u.id === userId);
              
              if (!user) return; // Skip if user not found
              
              // Initialize user workload if not exists
              if (!userWorkloads[userId]) {
                userWorkloads[userId] = {
                  userId: userId,
                  userName: user.name,
                  totalItems: 0,
                  overdue: 0,
                  dueSoon: 0,
                  items: [],
                  expanded: false
                };
              }
              
              // Get due date status
              const dueStatus = getDueDateStatus(step.due_date);
              
              // Add step to user's workload
              userWorkloads[userId].items.push({
                id: step.id,
                type: 'step',
                name: step.name,
                projectId: project.id,
                projectName: project.name,
                phaseId: phase.id,
                phaseName: phase.name,
                dueDate: step.due_date,
                dueStatus,
                status: step.status || 'Not set',
                departmentId: project.department_id,
                departmentName: departments.find(d => d.id === project.department_id)?.name || 'Unknown'
              });
              
              userWorkloads[userId].totalItems++;
              
              // Update due date counts
              if (dueStatus.color === 'red') {
                userWorkloads[userId].overdue++;
              } else if (dueStatus.color === 'orange') {
                userWorkloads[userId].dueSoon++;
              }
            });
          }
        }
      }
      
      // If we're looking at unassigned items, add them to the result
      if (selectedUserId === 'unassigned' || (selectedUserId === 'all' && unassignedItems.length > 0)) {
        const overdue = unassignedItems.filter(item => item.dueStatus.color === 'red').length;
        const dueSoon = unassignedItems.filter(item => item.dueStatus.color === 'orange').length;
        
        userWorkloads['unassigned'] = {
          userId: 'unassigned',
          userName: 'Unassigned Items',
          totalItems: unassignedItems.length,
          overdue,
          dueSoon,
          items: unassignedItems,
          expanded: false
        };
      }
      
      // Convert to array and sort by most items
      const workloadArray = Object.values(userWorkloads).sort((a, b) => b.totalItems - a.totalItems);
      console.log('Workload data for steps:', workloadArray);
      
      setWorkloadData(workloadArray);
      setLoading(false);
    } catch (error) {
      console.error('Error processing steps workload:', error);
      throw error;
    }
  };

  // Process phases workload data
  const processPhasesWorkload = async (projects, batchAssignments) => {
    try {
      // Map to track user workloads
      const userWorkloads = {};
      
      // Track unassigned items specifically
      const unassignedItems = [];
      
      // Process each project
      for (const project of projects) {
        // Skip if no assignments data
        if (!batchAssignments[project.id]) continue;
        
        // Process all phases in this project
        for (const phase of (project.phases || [])) {
          // Skip based on status (exclude completed/cancelled)
          const status = phase.status ? phase.status.toLowerCase() : '';
          if (status.includes('complete') || 
              status.includes('finished') || 
              status.includes('done') || 
              status.includes('cancel') || 
              status.includes('abandon')) {
            continue;
          }
          
          // Get assignments for this phase
          const phaseAssignments = batchAssignments[project.id]?.phases?.[phase.id]?.assignments;
          
          // Check for responsible assignments
          const responsibleAssignments = phaseAssignments?.R || [];
          
          // If there are no responsible assignments and we're showing unassigned
          if (responsibleAssignments.length === 0) {
            // Only include if we're specifically looking at unassigned or showing all
            if (selectedUserId === 'unassigned' || selectedUserId === 'all') {
              // Get due date status
              const dueStatus = getDueDateStatus(phase.due_date);
              
              // Add to unassigned items
              unassignedItems.push({
                id: phase.id,
                type: 'phase',
                name: phase.name,
                projectId: project.id,
                projectName: project.name,
                dueDate: phase.due_date,
                dueStatus,
                status: phase.status || 'Not set',
                departmentId: project.department_id,
                departmentName: departments.find(d => d.id === project.department_id)?.name || 'Unknown'
              });
            }
            continue;
          }
          
          // Filter by user if needed
          const filteredAssignments = selectedUserId === 'all' || selectedUserId === 'unassigned'
            ? responsibleAssignments
            : responsibleAssignments.filter(a => a.user_id === parseInt(selectedUserId));
          
          // Skip if no matching assignments
          if (filteredAssignments.length === 0) continue;
          
          // Process assignments for each user
          filteredAssignments.forEach(assignment => {
            const userId = assignment.user_id;
            const user = users.find(u => u.id === userId);
            
            if (!user) return; // Skip if user not found
            
            // Initialize user workload if not exists
            if (!userWorkloads[userId]) {
              userWorkloads[userId] = {
                userId: userId,
                userName: user.name,
                totalItems: 0,
                overdue: 0,
                dueSoon: 0,
                items: [],
                expanded: false
              };
            }
            
            // Get due date status
            const dueStatus = getDueDateStatus(phase.due_date);
            
            // Add phase to user's workload
            userWorkloads[userId].items.push({
              id: phase.id,
              type: 'phase',
              name: phase.name,
              projectId: project.id,
              projectName: project.name,
              dueDate: phase.due_date,
              dueStatus,
              status: phase.status || 'Not set',
              departmentId: project.department_id,
              departmentName: departments.find(d => d.id === project.department_id)?.name || 'Unknown'
            });
            
            userWorkloads[userId].totalItems++;
            
            // Update due date counts
            if (dueStatus.color === 'red') {
              userWorkloads[userId].overdue++;
            } else if (dueStatus.color === 'orange') {
              userWorkloads[userId].dueSoon++;
            }
          });
        }
      }
      
      // If we're looking at unassigned items, add them to the result
      if (selectedUserId === 'unassigned' || (selectedUserId === 'all' && unassignedItems.length > 0)) {
        const overdue = unassignedItems.filter(item => item.dueStatus.color === 'red').length;
        const dueSoon = unassignedItems.filter(item => item.dueStatus.color === 'orange').length;
        
        userWorkloads['unassigned'] = {
          userId: 'unassigned',
          userName: 'Unassigned Items',
          totalItems: unassignedItems.length,
          overdue,
          dueSoon,
          items: unassignedItems,
          expanded: false
        };
      }
      
      // Convert to array and sort by most items
      const workloadArray = Object.values(userWorkloads).sort((a, b) => b.totalItems - a.totalItems);
      console.log('Workload data for phases:', workloadArray);
      
      setWorkloadData(workloadArray);
      setLoading(false);
    } catch (error) {
      console.error('Error processing phases workload:', error);
      throw error;
    }
  };

  // Handle navigation to project/phase/step
  const handleItemClick = (item) => {
    if (item.type === 'step') {
      navigate(`/projects/${item.projectId}/steps/${item.id}`);
    } else if (item.type === 'phase') {
      navigate(`/projects/${item.projectId}/phases/${item.id}`);
    }
  };

  // Expand/collapse user workload details
  const toggleUserExpand = (userId) => {
    setWorkloadData(workloadData.map(user => {
      if (user.userId === userId) {
        return { ...user, expanded: !user.expanded };
      }
      return user;
    }));
  };

  // Generate calendar events from workload data
  useEffect(() => {
    if (workloadData.length === 0) {
      setCalendarEvents([]);
      return;
    }
    
    const events = [];
    
    // Process each user's workload
    workloadData.forEach(user => {
      // Process each item in the user's workload
      user.items.forEach(item => {
        if (!item.dueDate) return; // Skip items without due dates
        
        try {
          const eventDate = createCalendarDate(item.dueDate);
          if (!eventDate || isNaN(eventDate.getTime())) return; // Skip invalid dates
          
          // Special title for unassigned items
          const title = user.userId === 'unassigned' 
            ? `${item.name} (Unassigned)` 
            : `${item.name} (${getInitials(user.userName)})`;
          
          events.push({
            id: `${item.type}-${item.id}-${user.userId}`,
            title: title,
            start: eventDate,
            end: eventDate,
            allDay: true,
            resource: {
              item,
              user,
              dueDateColor: item.dueStatus.color,
              userColor: user.userId === 'unassigned' ? '#6B7280' : getColorForUser(user.userId), // Gray for unassigned
              isUnassigned: user.userId === 'unassigned'
            }
          });
        } catch (error) {
          console.error('Error creating calendar event:', error);
        }
      });
    });
    
    setCalendarEvents(events);
  }, [workloadData]);
  
  // Style event based on due date status and user
  const eventStyleGetter = (event) => {
    const resource = event.resource || {};
    
    // Get colors for styling
    const dueDateColor = resource.dueDateColor === 'red' ? '#EF4444' : 
                         resource.dueDateColor === 'orange' ? '#F97316' : 
                         resource.dueDateColor === 'green' ? '#22C55E' : '#6B7280';
    
    const userColor = resource.userColor || '#4B5563'; // gray-600 default
    
    // Custom style for unassigned items (dashed border)
    const borderStyle = resource.isUnassigned ? 'dashed' : 'solid';
    
    return {
      style: {
        backgroundColor: userColor,
        borderLeft: `4px ${borderStyle} ${dueDateColor}`,
        color: 'white',
        borderRadius: '4px',
        display: 'block',
        padding: '2px 5px'
      }
    };
  };

  // Handle click on event to navigate
  const handleEventClick = (event) => {
    const resource = event.resource;
    if (resource && resource.item) {
      handleItemClick(resource.item);
    }
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8" style={{ backgroundColor: '#111827', borderRadius: '8px', padding: '20px' }}>
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-2xl font-semibold text-white">Workload Report</h1>
          <p className="mt-2 text-sm text-gray-300">
            View workload distribution across your teams and projects.
          </p>
        </div>
      </div>

      <ViewsNavigation />

      {/* Filters */}
      <div className="mt-6 mb-6 grid grid-cols-1 md:grid-cols-4 gap-4 bg-gray-800 p-4 rounded-lg border border-gray-700">
        <div>
          <label htmlFor="department-filter" className="block text-sm font-medium text-gray-300 mb-1">
            Filter by Department
          </label>
          <select
            id="department-filter"
            value={selectedDepartmentId}
            onChange={(e) => setSelectedDepartmentId(e.target.value)}
            className="mt-1 block w-full rounded-md border-gray-600 bg-gray-700 text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
          >
            <option value="all">All Departments</option>
            {departments.map((department) => (
              <option key={department.id} value={department.id}>
                {department.name}
              </option>
            ))}
          </select>
        </div>
        
        <div>
          <label htmlFor="user-filter" className="block text-sm font-medium text-gray-300 mb-1">
            Filter by User
          </label>
          <select
            id="user-filter"
            value={selectedUserId}
            onChange={(e) => setSelectedUserId(e.target.value)}
            className="mt-1 block w-full rounded-md border-gray-600 bg-gray-700 text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
          >
            <option value="all">All Users</option>
            <option value="unassigned">Unassigned</option>
            {users.map((user) => (
              <option key={user.id} value={user.id}>
                {user.name}
              </option>
            ))}
          </select>
        </div>
        
        <div>
          <label htmlFor="view-selector" className="block text-sm font-medium text-gray-300 mb-1">
            Work Item Type
          </label>
          <select
            id="view-selector"
            value={view}
            onChange={(e) => setView(e.target.value)}
            className="mt-1 block w-full rounded-md border-gray-600 bg-gray-700 text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
          >
            <option value="steps">Steps</option>
            <option value="phases">Phases</option>
          </select>
        </div>
        
        <div>
          <label htmlFor="display-mode" className="block text-sm font-medium text-gray-300 mb-1">
            Display Mode
          </label>
          <div className="mt-1 flex rounded-md shadow-sm">
            <button
              type="button"
              onClick={() => setDisplayMode('list')}
              className={`relative inline-flex items-center px-4 py-2 rounded-l-md border border-gray-300 ${
                displayMode === 'list' 
                  ? 'bg-indigo-600 text-white' 
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              } text-sm font-medium focus:z-10 focus:outline-none focus:ring-1 focus:ring-indigo-500`}
            >
              List
            </button>
            <button
              type="button"
              onClick={() => setDisplayMode('calendar')}
              className={`relative inline-flex items-center px-4 py-2 rounded-r-md border border-gray-300 ${
                displayMode === 'calendar' 
                  ? 'bg-indigo-600 text-white' 
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              } text-sm font-medium focus:z-10 focus:outline-none focus:ring-1 focus:ring-indigo-500`}
            >
              Calendar
            </button>
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="mb-6 bg-gray-700 p-4 rounded-md shadow-sm border border-gray-600">
        <h3 className="text-sm font-medium text-white mb-2">Due Date Status:</h3>
        <div className="flex space-x-4">
          <div className="flex items-center">
            <div className="w-4 h-4 bg-green-100 rounded mr-2"></div>
            <span className="text-sm text-gray-300">Due in more than 7 days</span>
          </div>
          <div className="flex items-center">
            <div className="w-4 h-4 bg-orange-100 rounded mr-2"></div>
            <span className="text-sm text-gray-300">Due within 7 days</span>
          </div>
          <div className="flex items-center">
            <div className="w-4 h-4 bg-red-100 rounded mr-2"></div>
            <span className="text-sm text-gray-300">Overdue</span>
          </div>
        </div>
      </div>

      {/* Workload Report */}
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
        </div>
      ) : error ? (
        <div className="bg-red-900 bg-opacity-20 p-4 rounded-md">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-red-300">{error}</p>
            </div>
          </div>
        </div>
      ) : workloadData.length === 0 ? (
        <div className="bg-gray-800 shadow overflow-hidden sm:rounded-md p-6 text-center border border-gray-700">
          <div className="text-gray-300 mb-4">
            No workload data found for {view === 'steps' ? 'steps' : 'phases'} with the selected filters.
          </div>
          <div className="text-sm text-gray-300 bg-yellow-900 bg-opacity-20 p-4 rounded-md">
            <p className="font-medium mb-2 text-yellow-200">Possible reasons:</p>
            <ul className="list-disc list-inside">
              <li>No {view === 'steps' ? 'steps' : 'phases'} have been assigned with "Responsible (R)" roles</li>
              <li>All items with "R" assignments have been completed or cancelled</li>
              <li>The selected department or user filters are too restrictive</li>
            </ul>
            <p className="mt-4 text-yellow-200">Try switching to {view === 'steps' ? 'phases' : 'steps'} view or changing your filters.</p>
          </div>
        </div>
      ) : displayMode === 'calendar' ? (
        <div className="bg-gray-800 p-4 rounded-md shadow-sm border border-gray-700">
          {/* Calendar View Controls */}
          <div className="mb-4 flex justify-between items-center">
            <div className="text-lg font-medium text-white">
              Calendar View: {view === 'steps' ? 'Steps' : 'Phases'} Due Dates
            </div>
            <div className="inline-flex rounded-md shadow-sm">
              <button
                onClick={() => setCalendarView('month')}
                className={`px-4 py-2 text-sm font-medium ${
                  calendarView === 'month' 
                    ? 'bg-indigo-600 text-white' 
                    : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                } rounded-l-md border border-gray-300 focus:z-10 focus:outline-none focus:ring-1 focus:ring-indigo-500`}
              >
                Month
              </button>
              <button
                onClick={() => setCalendarView('week')}
                className={`px-4 py-2 text-sm font-medium ${
                  calendarView === 'week' 
                    ? 'bg-indigo-600 text-white' 
                    : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                } border-t border-b border-gray-300 focus:z-10 focus:outline-none focus:ring-1 focus:ring-indigo-500`}
              >
                Week
              </button>
              <button
                onClick={() => setCalendarView('day')}
                className={`px-4 py-2 text-sm font-medium ${
                  calendarView === 'day' 
                    ? 'bg-indigo-600 text-white' 
                    : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                } rounded-r-md border border-gray-300 focus:z-10 focus:outline-none focus:ring-1 focus:ring-indigo-500`}
              >
                Day
              </button>
            </div>
          </div>
          
          {/* Calendar */}
          <div className="h-[700px]">
            <Calendar
              localizer={localizer}
              events={calendarEvents}
              startAccessor="start"
              endAccessor="end"
              style={{ height: '100%' }}
              views={['month', 'week', 'day']}
              view={calendarView}
              onView={setCalendarView}
              eventPropGetter={eventStyleGetter}
              onSelectEvent={handleEventClick}
              popup
            />
          </div>
        </div>
      ) : (
        <div className="bg-white shadow overflow-hidden sm:rounded-md">
          <ul className="divide-y divide-gray-200">
            {workloadData.map((user) => (
              <li key={user.userId} className="block hover:bg-gray-50">
                <div className="px-4 py-4 sm:px-6">
                  <div className="flex items-center justify-between cursor-pointer" onClick={() => toggleUserExpand(user.userId)}>
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <div className="h-10 w-10 rounded-full bg-indigo-100 flex items-center justify-center">
                          <span className="text-indigo-800 font-medium">
                            {user.userName.split(' ').map(name => name[0]).join('')}
                          </span>
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-indigo-600">{user.userName}</div>
                        <div className="text-sm text-gray-500">
                          {user.totalItems} {view === 'steps' ? 'step' : 'phase'}{user.totalItems !== 1 ? 's' : ''}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center">
                      {user.overdue > 0 && (
                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800 mr-2">
                          {user.overdue} overdue
                        </span>
                      )}
                      {user.dueSoon > 0 && (
                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-orange-100 text-orange-800 mr-2">
                          {user.dueSoon} due soon
                        </span>
                      )}
                      <svg className={`h-5 w-5 text-gray-400 ${user.expanded ? 'transform rotate-180' : ''}`} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                        <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </div>
                  </div>
                  
                  {user.expanded && (
                    <div className="mt-4 border-t border-gray-200 pt-4">
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Name
                              </th>
                              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Project
                              </th>
                              {view === 'steps' && (
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Phase
                                </th>
                              )}
                              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Department
                              </th>
                              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Due Date
                              </th>
                              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Status
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {user.items.map((item) => (
                              <tr 
                                key={`${item.type}-${item.id}`} 
                                className="hover:bg-gray-50 cursor-pointer"
                                onClick={() => handleItemClick(item)}
                              >
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                  {item.name}
                                  <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                    {item.type}
                                  </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                  {item.projectName}
                                </td>
                                {view === 'steps' && (
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                    {item.phaseName}
                                  </td>
                                )}
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                  {item.departmentName}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-${item.dueStatus.color}-100 text-${item.dueStatus.color}-800`}>
                                    {item.dueStatus.text}
                                  </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                  {item.status || 'No status'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export default WorkloadReport; 